import React, { useState } from 'react';
import { Project } from '../../../types';
import { useKanbanStore } from '../../../store/useKanbanStore';

interface ProjectSidebarProps {
  projects: Project[];
  activeProjectId: string;
  onSelectProject: (id: string) => void;
  mobileOpen?: boolean;
  onMobileOpenChange?: (open: boolean) => void;
}

export const ProjectSidebar: React.FC<ProjectSidebarProps> = ({
  projects,
  activeProjectId,
  onSelectProject,
  mobileOpen = false,
  onMobileOpenChange,
}) => {
  // Pull feature flags and toggles from store (with fallbacks if undefined)
  const store = useKanbanStore() as any;
  const { addProject, updateProject } = store;
  const userPermissions = store.userPermissions || (() => ({ isOwner: true }));
  
  const showTime = store.showTime ?? true;
  const showCost = store.showCost ?? true;
  const showRawHours = store.showRawHours ?? false;
  const toggleShowTime = store.toggleShowTime || (() => {});
  const toggleShowCost = store.toggleShowCost || (() => {});
  const toggleShowHours = store.toggleShowHours || (() => {});
  const searchQuery = store.searchQuery ?? '';
  const setSearchQuery = store.setSearchQuery || (() => {});

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Keep the desktop collapse state separate from the mobile drawer state.
  // The drawer is closed by default on phones, while the sidebar remains a
  // static flex item on desktop.
  React.useEffect(() => {
    if (mobileOpen) setIsCollapsed(false);
  }, [mobileOpen]);

  const handleSelectProject = (projectId: string) => {
    onSelectProject(projectId);
    onMobileOpenChange?.(false);
  };

  const activeProjects = projects.filter((p) => !p.archived);
  const archivedProjects = projects.filter((p) => p.archived);

  const handleCreateProject = () => {
    const name = prompt('Enter project name:');
    if (!name) return;
    addProject({
      name,
      color: '#3b82f6',
      category: 'general',
      tags: [],
    });
  };

  const handleRenameProject = (e: React.MouseEvent, project: Project) => {
    e.stopPropagation();
    const name = prompt('Rename project:', project.name);
    if (!name || name === project.name) return;
    updateProject(project.id, { name });
  };

  const handleToggleArchive = (e: React.MouseEvent, project: Project) => {
    e.stopPropagation();
    const isArchiving = !project.archived;
    const confirmMsg = isArchiving
      ? `Archive project "${project.name}"?`
      : `Unarchive project "${project.name}"?`;

    if (window.confirm(confirmMsg)) {
      updateProject(project.id, { archived: isArchiving });

      if (isArchiving && project.id === activeProjectId) {
        const nextActive = activeProjects.find((p) => p.id !== project.id);
        if (nextActive) {
          onSelectProject(nextActive.id);
        }
      }
    }
  };

  const handleColorChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    projectId: string
  ) => {
    e.stopPropagation();
    const newColor = e.target.value;
    updateProject(projectId, { color: newColor });
  };

  const renderProjectItem = (proj: Project, isArchivedList = false) => {
    const isActive = proj.id === activeProjectId;
    const isOwner = userPermissions(proj.id).isOwner;
    const currentColor = proj.color || '#3b82f6';

    return (
      <div
        key={proj.id}
        onClick={() => handleSelectProject(proj.id)}
        title={isCollapsed ? proj.name : undefined}
        className={`w-full group flex items-center ${
          isCollapsed ? 'justify-center p-2' : 'justify-between px-3 py-2'
        } rounded-md text-sm cursor-pointer transition-colors ${
          isActive
            ? 'bg-blue-600 text-white font-medium'
            : 'hover:bg-slate-700/60 text-slate-300'
        }`}
      >
        <div className="flex items-center gap-2 truncate min-w-0">
          <div
            className="relative group/color shrink-0"
            title="Click to change project color"
            onClick={(e) => e.stopPropagation()}
          >
            <span
              className="block w-2.5 h-2.5 rounded-full shadow-sm transition-transform group-hover/color:scale-125"
              style={{ backgroundColor: currentColor }}
            />
            <input
              type="color"
              value={currentColor}
              onChange={(e) => handleColorChange(e, proj.id)}
              className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
            />
          </div>

          {!isCollapsed && (
            <span className={`truncate ${isArchivedList ? 'line-through text-slate-400' : ''}`}>
              {proj.name}
            </span>
          )}
        </div>

        {!isCollapsed && isOwner && (
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => handleRenameProject(e, proj)}
              className="text-xs text-slate-400 hover:text-white px-1"
              title="Rename Project"
            >
              ✏️
            </button>
            <button
              onClick={(e) => handleToggleArchive(e, proj)}
              className="text-xs text-slate-400 hover:text-amber-400 px-1"
              title={isArchivedList ? 'Unarchive Project' : 'Archive Project'}
            >
              {isArchivedList ? '📤' : '📦'}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => onMobileOpenChange?.(false)}
          aria-hidden="true"
        />
      )}
      <aside
        className={`fixed md:relative inset-y-0 left-0 z-40 md:z-auto transform transition-transform duration-300 ease-in-out ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        } ${isCollapsed ? 'w-64 md:w-16' : 'w-64 md:w-64'} bg-slate-800 text-slate-200 flex flex-col shrink-0 border-r border-slate-700`}
      >
      {/* Header */}
      <div className="p-4 font-bold text-lg text-white flex justify-between items-center">
        {!isCollapsed && <span className="truncate">Kan-Do</span>}
        <div className="flex items-center gap-1.5 ml-auto">
          {!isCollapsed && (
            <button
              onClick={handleCreateProject}
              className="text-xs bg-blue-600 hover:bg-blue-500 text-white px-2.5 py-1 rounded transition-colors"
              title="Create New Project"
            >
              + New
            </button>
          )}
          <button
            onClick={() => onMobileOpenChange?.(false)}
            className="md:hidden p-1 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors"
            title="Close Sidebar"
            aria-label="Close sidebar"
          >
            ✕
          </button>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="hidden md:block p-1 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors"
            title={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? '≫' : '≪'}
          </button>
        </div>
      </div>

      {/* Search Bar (Below title, above dividing line) */}
      {!isCollapsed ? (
        <div className="px-3 pb-3">
          <div className="relative flex items-center">
            <span className="absolute left-2.5 text-xs text-slate-400 pointer-events-none">🔍</span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search tasks, tags..."
              className="w-full pl-8 pr-7 py-1.5 text-xs bg-slate-900/60 border border-slate-700 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-slate-200 placeholder-slate-400"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2 text-slate-400 hover:text-white text-xs"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="px-2 pb-3 flex justify-center" title="Search Tasks">
          <button
            onClick={() => setIsCollapsed(false)}
            className="p-1.5 text-slate-400 hover:text-white bg-slate-900/60 rounded border border-slate-700 text-xs"
          >
            🔍
          </button>
        </div>
      )}

      {/* Dividing Line */}
      <div className="border-t border-slate-700"></div>

      {/* Active Projects List */}
      <div className="flex-1 overflow-y-auto p-3 space-y-1">
        {!isCollapsed && (
          <p className="text-xs font-semibold text-slate-400 px-2 py-1 uppercase tracking-wider">
            Projects ({activeProjects.length})
          </p>
        )}
        {activeProjects.map((proj) => renderProjectItem(proj, false))}
      </div>

      {/* Archived Projects Section */}
      {archivedProjects.length > 0 && (
        <div className="border-t border-slate-700 p-2 bg-slate-850">
          {!isCollapsed ? (
            <>
              <button
                onClick={() => setShowArchived(!showArchived)}
                className="w-full flex items-center justify-between text-xs font-semibold text-slate-400 hover:text-slate-200 py-1 px-2 uppercase tracking-wider"
              >
                <span>Archived ({archivedProjects.length})</span>
                <span>{showArchived ? '▼' : '▶'}</span>
              </button>

              {showArchived && (
                <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                  {archivedProjects.map((proj) => renderProjectItem(proj, true))}
                </div>
              )}
            </>
          ) : (
            <div className="flex justify-center" title={`${archivedProjects.length} Archived Projects`}>
              <span className="text-xs text-slate-500">📦</span>
            </div>
          )}
        </div>
      )}

      {/* Feature Settings Toggle Panel */}
      <div className="border-t border-slate-700 p-3 bg-slate-800/80">
        {!isCollapsed ? (
          <div>
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="w-full flex items-center justify-between text-xs font-semibold text-slate-400 hover:text-slate-200 py-1 uppercase tracking-wider"
            >
              <span>⚙️ View Options</span>
              <span>{showSettings ? '▼' : '▲'}</span>
            </button>

            {showSettings && (
              <div className="mt-2 pt-2 border-t border-slate-700/60 space-y-2 text-xs">
                <label className="flex items-center justify-between text-slate-300 cursor-pointer hover:text-white">
                  <span>Show Time Features</span>
                  <input
                    type="checkbox"
                    checked={showTime}
                    onChange={toggleShowTime}
                    className="rounded bg-slate-700 border-slate-600 text-blue-600 focus:ring-0 cursor-pointer"
                  />
                </label>

                <label className="flex items-center justify-between text-slate-300 cursor-pointer hover:text-white">
                  <span>Show Cost Features</span>
                  <input
                    type="checkbox"
                    checked={showCost}
                    onChange={toggleShowCost}
                    className="rounded bg-slate-700 border-slate-600 text-blue-600 focus:ring-0 cursor-pointer"
                  />
                </label>

                {showTime && (
                  <label className="flex items-center justify-between gap-2 text-slate-300 cursor-pointer hover:text-white pl-2">
                    <span>Show Hours (not days/weeks)</span>
                    <input
                      type="checkbox"
                      checked={showRawHours}
                      onChange={toggleShowHours}
                      className="rounded bg-slate-700 border-slate-600 text-blue-600 focus:ring-0 cursor-pointer"
                    />
                  </label>
                )}
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={() => {
              setIsCollapsed(false);
              setShowSettings(true);
            }}
            className="w-full flex justify-center text-slate-400 hover:text-white"
            title="View Options"
          >
            ⚙️
          </button>
        )}
      </div>
      </aside>
    </>
  );
};